import React, {useEffect, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import {useParams} from "react-router";
import projectUtils from "../../../../../utils/project-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import materialGroupService from "../../../../../services/web/material-group-service.js";
import {FaPlus} from "react-icons/fa";
import useFetchOptions from "../../hooks/use-fetch-options.js";
import materialXStorageService from "../../../../../services/web/material-x-storage-service.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import Attach from "../../../../../components/attach/attach.jsx";
import materialService from "../../../../../services/web/material-service.js";
import materialDecreaseService from "../../../../../services/web/material-decrease-service.js";

const initialData = {
    id: "",
    storage_id: "",
    group: "",
    quantity: "",
    material_x_storage_id: "",
    decrease_term: "",
    observations: "",
}

const MaterialDecreasesForm = () => {
    const navigate = navigateHook();
    const {id} = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState(initialData)
    const [materialDescription, setMaterialDescription] = useState("")
    const [canEditDecreaseTerm, setCanEditDecreaseTerm] = useState(false)
    const {storageByUserOptions} = useFetchOptions({fetchStorageByUser: true})
    const isEditing = !!id

    const onChangeSelect = (id, value) => {
        if (id === "storage_id") setCanEditDecreaseTerm(false)
        setData(prev => ({...prev, [id]: value || ""}));
    }

    const onChangeFile = (file, id) => {
        if (file) setData(prev => ({...prev, [id]: file}))
    }

    const removeFile = (id) => setData(prev => ({...prev, [id]: ""}))

    const {data: decrease, refetch, status} = useQuery({
        queryKey: ['material-decrease'],
        queryFn: () => materialDecreaseService.getById(id),
        enabled: !!id
    });

    useEffect(() => {
        if (status === "success") {
            handleData(decrease).then(() => {})
            setCanEditDecreaseTerm(decrease.material?.decrease_term === true && decrease.decrease_term === null)
        }
    }, [decrease, status]);

    const handleData = async (decrease) => {
        const {storage_id, quantity, material_x_storage_id, decrease_term, observations, material_id} = decrease

        const {group_id, description} = await materialService.getById(material_id)

        const newData = {
            storage_id,
            group: group_id,
            quantity,
            material_x_storage_id,
            decrease_term,
            observations,
        }

        const decreaseNormalized = projectUtils.normalizeData(newData)
        setData(prevState => ({...prevState, ...decreaseNormalized}))
        setMaterialDescription(description)
    }

    const {data: groups, isLoading: isLoadingGroups} = useQuery({
        queryKey: ['material-groups', data.storage_id],
        queryFn: () => materialGroupService.options(data.storage_id),
        enabled: !!data.storage_id
    });

    useEffect(() => {
        const groupSelected = groups && groups.length === 1 ? groups[0].value : ""
        setData(prev => ({...prev, group: groupSelected}))
    }, [groups]);

    const {data: materials, isLoading: isLoadingMaterials} = useQuery({
        queryKey: ['materials', data.group, data.storage_id],
        queryFn: () => materialXStorageService.optionsAvailableByGroupAndStorage(data.group, data.storage_id, false),
        enabled: !!data.group && !!data.storage_id
    });

    const {data: materialSelected, isLoading: isLoadingMaterial} = useQuery({
        queryKey: ['material', data.material_x_storage_id],
        queryFn: async () => {
            const {material_id} = materials.find(m => m.value === data.material_x_storage_id)
            return materialService.getById(material_id)
        },
        enabled: !!data.material_x_storage_id
    });

    useEffect(() => {
        if (materialSelected) {
            if (materialSelected.is_unique) setData(prev => ({...prev, quantity: 1}))
            setCanEditDecreaseTerm(materialSelected?.decrease_term === true)
        } else {
            setCanEditDecreaseTerm(false)
        }
    }, [materialSelected]);

    const save = () => {
        setIsLoading(true);

        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });

        materialDecreaseService.save(formData, id)
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch()
                    return;
                }

                const urlDev = `/materials/decreases/form/${data.id}`;
                navigate(urlDev);

            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    const openForm = () => {
        const urlDev = `/materials/decreases/form`;
        setData(initialData)
        setMaterialDescription("")

        navigate(urlDev)
    }

    return (<Template isLoading={isLoading || isLoadingGroups || isLoadingMaterials || isLoadingMaterial}>
        <Template.Header>
            <Template.Title>
                Baixa
            </Template.Title>
            {
                id &&
                <Button target={"primary-outline"} onClick={openForm}>
                    <FaPlus/><span className="font-bold">Adicionar baixa</span>
                </Button>
            }
        </Template.Header>
        <Template.Body>
            <Card>
                <Card.Body>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3">
                            <SelectComponent
                                isDisabled={!!id}
                                id="storage_id"
                                name="storage_id"
                                value={data.storage_id}
                                options={storageByUserOptions || []}
                                label={"Depósito *"}
                                onChange={(selected) => onChangeSelect("storage_id", selected?.value)}
                            />
                        </div>

                        <div className="col-span-2">
                            <SelectComponent
                                isDisabled={!!id}
                                id="group"
                                name="group"
                                value={data.group}
                                options={groups || []}
                                label={"Grupo *"}
                                onChange={(selected) => onChangeSelect("group", selected?.value)}
                            />
                        </div>
                        {
                            id &&
                            <div className={"col-span-3"}>
                                <InputForm disabled={true} value={materialDescription} label="Material *" onChange={() => {}}/>
                            </div>
                        }
                        {
                            !id &&
                            <div className={"col-span-3"}>
                                <SelectComponent
                                    id="material_x_storage_id"
                                    name="material_x_storage_id"
                                    value={data.material_x_storage_id}
                                    options={materials || []}
                                    label={"Material *"}
                                    onChange={(selected) => onChangeSelect("material_x_storage_id", selected?.value)}
                                />
                            </div>
                        }

                        <div className={"col-span-2"}>
                            <InputForm
                                disabled={(materialSelected && materialSelected.is_unique) || (!!id)}
                                value={data.quantity}
                                id={"quantity"}
                                name={"quantity"}
                                label="Quantidade *"
                                onChange={({target: {id, value}}) => setData(prev => ({...prev, [id]: value}))}/>
                        </div>

                        {
                            <div className="col-span-2">
                                <Attach
                                    attachedButtonDisabled={!canEditDecreaseTerm}
                                    showRemove={!isEditing || canEditDecreaseTerm}
                                    onChangeFile={(file) => onChangeFile(file, "decrease_term")}
                                    removeFile={() => removeFile("decrease_term")}
                                    itemId={"decrease_term"}
                                    attachId={data.decrease_term}
                                    attachedButtonName={"Termo de baixa"}
                                    modalTitle={"Termo de baixa"}
                                />
                            </div>
                        }

                        <div className={"col-span-12"}>
                            <InputTextarea
                                disabled={!!id}
                                id={"observations"}
                                className={"col-span-full"}
                                textAreaClass={"h-20"}
                                label={"Observação"}
                                value={data.observations}
                                onChange={({target: {value, id}}) => setData(prev => ({...prev, [id]: value}))}
                            />
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {
                (!id || canEditDecreaseTerm) &&
                <Card className={"mt-2"}>
                    <Card.Body>
                        <div className="flex justify-end gap-2 w-full">
                            <Button onClick={save} actionFeedbackType='save'>Salvar</Button>
                        </div>
                    </Card.Body>
                </Card>
            }
        </Template.Body>
    </Template>);
};

export default MaterialDecreasesForm;